import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SearchWidget from "@/components/SearchWidget";
import http from "@/lib/api";
import { useT } from "@/lib/i18n";
import { SmartImage } from "@/components/ImageUploadField";
import { ShieldCheck, Tag, Clock, Wallet, Ship, Compass } from "lucide-react";
import Seo from "@/components/Seo";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/51eb26e7-3b26-4eab-bc27-818b526e83a2/images/4188bb7cb89aadd69d6fb500c13a15b5f64d6e78c61e74b736ff407a2946df64.png";

export default function Home() {
  const { t } = useT();
  const [destinations, setDestinations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    http.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
    http.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--bone))]" data-testid="home-page">
      <Seo
        title={null}
        description="Türkiye'den Yunan Adaları'na — feribot bileti, otel rezervasyonu, tur, araç kiralama ve vize için tek adres. En iyi fiyat garantisi, 2 dakikada bilet."
      />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <SmartImage
            src={HERO_IMG}
            alt="Aegean villa"
            className="w-full h-full object-cover"
            width={1920}
            height={1080}
            quality={70}
            eager
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-[hsl(var(--bone))]" />
        </div>

        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-12 md:pb-16 z-10">
          <div className="max-w-3xl text-white anim-fade-up">
            <p className="label-tiny opacity-80 mb-3">{t("hero.kicker")}</p>
            <h1 className="h-display text-[clamp(1.9rem,4vw,3.4rem)] text-white mb-3">
              {t("hero.titleA")} <em className="italic font-normal">{t("hero.titleB")}</em>{" "}
              {t("hero.titleC")}
            </h1>
            <p className="hidden md:block text-white/85 text-base max-w-xl mb-4 leading-relaxed">
              {t("hero.sub")}
            </p>
          </div>

          {/* Search Widget overlaps hero bottom */}
          <div className="mt-5 md:mt-6 max-w-[1240px] anim-fade-up" style={{ animationDelay: "0.2s" }}>
            <SearchWidget />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[hsl(var(--border))] bg-white">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Tag, t: t("trust.bestPrice"), s: t("trust.bestPriceSub") },
            { icon: Clock, t: t("trust.fastBuy"), s: t("trust.fastBuySub") },
            { icon: ShieldCheck, t: t("trust.securePay"), s: t("trust.securePaySub") },
            { icon: Wallet, t: t("trust.loyalty"), s: t("trust.loyaltySub") },
          ].map(({ icon: Icon, t: title, s }) => (
            <div key={title} className="flex items-start gap-4">
              <Icon className="w-7 h-7 stroke-[1.4] text-[hsl(var(--terracotta))]" />
              <div>
                <div className="font-serif text-lg leading-tight">{title}</div>
                <div className="text-sm text-[hsl(var(--muted-fg))] mt-1">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DESTINATIONS (Bento) */}
      <section className="max-w-[1480px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="destinations-section">
        <div className="grid md:grid-cols-12 gap-10 mb-16 items-end">
          <div className="md:col-span-7">
            <p className="label-tiny text-[hsl(var(--terracotta))] mb-3">{t("sec.destinations")}</p>
            <h2 className="h-display text-[clamp(2rem,4vw,3.4rem)]">
              {t("sec.destinationsTitle")}
            </h2>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/destinasyonlar" className="label-tiny border-b border-[hsl(var(--ink))] pb-1" data-testid="all-destinations-link">
              {t("sec.allDestinations")}
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {destinations.slice(0, 6).map((d, i) => {
            const span = i === 0 ? "md:col-span-8 md:row-span-2" : i === 1 ? "md:col-span-4" : "md:col-span-4";
            const h = i === 0 ? "h-[560px]" : "h-[270px]";
            return (
              <Link key={d.slug} to={`/destinasyon/${d.slug}`} className={`${span} relative group img-zoom`} data-testid={`destination-${d.slug}`}>
                <div className={`relative ${h} overflow-hidden`}>
                  <SmartImage src={d.image} alt={d.name} width={640} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0" />
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <p className="label-tiny opacity-80">{d.tagline}</p>
                    <h3 className="font-serif text-3xl md:text-4xl mt-1">{d.name}</h3>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CAMPAIGNS */}
      <section className="bg-[hsl(var(--surface))] py-24 md:py-32">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="label-tiny text-[hsl(var(--terracotta))] mb-3">{t("sec.campaigns")}</p>
              <h2 className="h-display text-[clamp(2rem,4vw,3.4rem)]">{t("sec.weekFeatured")}</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {campaigns.map((c) => (
              <div key={c.id} className="card-flat p-0 overflow-hidden img-zoom" data-testid={`campaign-${c.code}`}>
                <div className="h-56 overflow-hidden">
                  <SmartImage src={c.image} alt={c.title} width={640} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <p className="label-tiny text-[hsl(var(--terracotta))] mb-3">Kod: {c.code}</p>
                  <h3 className="font-serif text-2xl mb-3">{c.title}</h3>
                  <p className="text-sm text-[hsl(var(--muted-fg))] leading-relaxed">{c.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1480px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="label-tiny text-[hsl(var(--terracotta))] mb-3">{t("sec.services")}</p>
            <h2 className="h-display text-[clamp(2rem,4vw,3.2rem)]">
              {t("sec.servicesTitle")}
            </h2>
            <div className="accent-line mt-6" />
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { to: "/feribot", icon: Ship, t: "Feribot Bileti", s: "Türkiye–Yunanistan tüm operatörler" },
              { to: "/otel", icon: Compass, t: "Seçilmiş Oteller", s: "Yerel fiyatlarla butik konaklama" },
              { to: "/tur", icon: Compass, t: "Tur ve Tatil Paketleri", s: "Rehberli & paket tatil" },
              { to: "/arackiralama", icon: Compass, t: "Araç Kiralama", s: "Adalarda hazır araç" },
              { to: "/vize", icon: Compass, t: "Schengen & Kapı Vizesi", s: "Profesyonel danışman" },
            ].map(({ to, icon: Icon, t, s }) => (
              <Link to={to} key={t} className="card-flat p-7 flex items-start gap-4" data-testid={`service-${t}`}>
                <Icon className="w-6 h-6 stroke-[1.4] mt-1 text-[hsl(var(--primary))]" />
                <div>
                  <h3 className="font-serif text-xl mb-1">{t}</h3>
                  <p className="text-sm text-[hsl(var(--muted-fg))]">{s}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE INFO STRIP */}
      <section className="bg-[hsl(var(--surface))] border-y border-[hsl(var(--border))]" data-testid="corp-info">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-8 grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
          <div>
            <p className="label-tiny text-[hsl(var(--terracotta))] mb-2">Kurumsal</p>
            <p className="text-sm font-medium">Scrup Turizm Seyahat Ve Bilişim Ticaret Limited Şirketi</p>
          </div>
          <div>
            <p className="label-tiny text-[hsl(var(--muted-fg))] mb-2">Adres</p>
            <p className="text-sm">Cumhuriyet Mah. Hükümet Cad. 17/231 Fethiye - Muğla</p>
          </div>
          <div>
            <p className="label-tiny text-[hsl(var(--muted-fg))] mb-2">İletişim</p>
            <a href="tel:+905453249892" className="text-sm font-medium block">0545 324 98 92</a>
            <Link to="/iletisim" className="text-xs underline text-[hsl(var(--muted-fg))] mt-1 inline-block">İletişim sayfası →</Link>
          </div>
          <div>
            <p className="label-tiny text-[hsl(var(--muted-fg))] mb-2">Belge</p>
            <p className="text-sm font-medium">TURSAB No: 17559</p>
          </div>
        </div>
      </section>

      {/* Partnerlerimiz */}
      <section className="max-w-[1480px] mx-auto px-6 md:px-10 py-14" data-testid="partners-section">
        <p className="label-tiny text-[hsl(var(--muted-fg))] text-center mb-8">PARTNERLERİMİZ</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-8">
          {["partner1", "partner2", "partner3", "partner4", "partner5", "partner6", "partner7", "partner8", "partner9", "partner10"].map((p) => (
            <img
              key={p}
              src={`/partners/${p}.png`}
              alt="Partner logosu"
              loading="lazy"
              className="h-10 md:h-12 w-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
              data-testid={`partner-logo-${p}`}
            />
          ))}
        </div>
      </section>

      <Footer />

    </div>
  );
}
